import type { ReactNode } from 'react'
import { Badge } from './ui'

interface Props {
  index: number
  title: string
  description?: string
  required: boolean
  satisfied: boolean
  /** Highlighted after a failed Ready? attempt so the gap is obvious. */
  highlighted?: boolean
  /**
   * Right-aligned controls in the header. The admin page puts its reorder,
   * edit and delete buttons here; the recipient page passes nothing and renders
   * exactly as before.
   */
  actions?: ReactNode
  children: ReactNode
}

export function SectionCard({
  index,
  title,
  description,
  required,
  satisfied,
  highlighted,
  actions,
  children,
}: Props) {
  return (
    <section
      className={`card p-5 transition-colors sm:p-6 ${
        highlighted ? 'border-amber-400 ring-2 ring-amber-200 dark:ring-amber-900' : ''
      }`}
    >
      <header className="mb-4 flex items-start gap-3">
        <div
          className={`mt-0.5 grid size-7 shrink-0 place-items-center rounded-full text-xs font-bold ${
            satisfied
              ? 'bg-emerald-500 text-white'
              : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400'
          }`}
        >
          {satisfied ? <CheckGlyph /> : index}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold">{title}</h2>
            {required ? (
              satisfied ? (
                <Badge tone="done">Required · done</Badge>
              ) : (
                <Badge tone="required">Required</Badge>
              )
            ) : (
              <Badge>Optional</Badge>
            )}
          </div>
          {description && (
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{description}</p>
          )}
        </div>
        {actions && <div className="flex shrink-0 gap-1 text-xs">{actions}</div>}
      </header>
      {children}
    </section>
  )
}

function CheckGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden>
      <path d="m5 13 4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
